import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
import { getAccountById } from '../../services/firestoreService';
import { getMarketingFulfillmentData } from '../../services/marketingFulfillmentDataService';
import { collection, query, where, getDocs, doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../firebase';
import { FaArrowLeft, FaKey } from 'react-icons/fa';
import PortalBackground from '../../components/PortalBackground';
import BBALoader from '../../components/BBALoader';

const MarketingFulfillmentReviewsClient = () => {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const { darkMode } = useTheme();
  const [account, setAccount] = useState(null);
  const [fulfillmentData, setFulfillmentData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [status, setStatus] = useState('Not Started');
  const [savingStatus, setSavingStatus] = useState(false);
  const [requestId, setRequestId] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!clientId) {
        setLoading(false);
        return;
      }
      setLoading(true);
      setError(null);
      try {
        const acct = await getAccountById(clientId);
        if (!acct || cancelled) return;
        setAccount(acct);

        const requestsRef = collection(db, 'marketingRequests');
        const reqQuery = query(
          requestsRef,
          where('category', '==', 'reviews'),
          where('accountId', '==', clientId)
        );
        const reqSnap = await getDocs(reqQuery);
        if (!cancelled && !reqSnap.empty) {
          const docs = reqSnap.docs.slice();
          docs.sort((a, b) => {
            const ad = a.data();
            const bd = b.data();
            const aTime = ad.createdAt?.toDate ? ad.createdAt.toDate().getTime() : (ad.createdAt ? new Date(ad.createdAt).getTime() : 0);
            const bTime = bd.createdAt?.toDate ? bd.createdAt.toDate().getTime() : (bd.createdAt ? new Date(bd.createdAt).getTime() : 0);
            return bTime - aTime;
          });
          const latest = docs[0];
          const latestData = latest.data();
          setRequestId(latest.id);
          setStatus(latestData.reviewsStatus || latestData.status || 'Not Started');
        }

        const data = await getMarketingFulfillmentData(clientId);
        if (!cancelled) setFulfillmentData(data);
      } catch (err) {
        if (!cancelled) {
          console.error('Error loading reviews client data:', err);
          setError(err.message || 'Failed to load data');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [clientId]);

  const reviews = fulfillmentData?.reviews || {};

  const handleSaveStatus = async () => {
    if (!requestId) return;
    try {
      setSavingStatus(true);
      const requestRef = doc(db, 'marketingRequests', requestId);
      await updateDoc(requestRef, {
        reviewsStatus: status,
        updatedAt: serverTimestamp()
      });
    } catch (err) {
      console.error('Error updating reviews status:', err);
    } finally {
      setSavingStatus(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6 relative">
        <PortalBackground />
        <div className="relative text-center">
          <BBALoader size={48} className="mx-auto" />
          <p className={`mt-4 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>Loading client data...</p>
        </div>
      </div>
    );
  }

  if (error || !clientId) {
    return (
      <div className="min-h-screen p-6 flex items-center justify-center relative">
        <PortalBackground />
        <div className="relative rounded-2xl p-6 max-w-md bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm">
          <p className="text-red-600 dark:text-red-400">{error || 'Client not found.'}</p>
          <button
            type="button"
            onClick={() => navigate('/crm/fulfillment/marketing')}
            className={`mt-4 px-4 py-2 rounded-md ${darkMode ? 'bg-gray-600 hover:bg-gray-500 text-white' : 'bg-gray-200 hover:bg-gray-300 text-gray-900'}`}
          >
            Back to Marketing Fulfillment
          </button>
        </div>
      </div>
    );
  }

  const companyName = account?.company || 'Client';

  return (
    <div className="min-h-screen p-6 relative">
      <PortalBackground />
      <div className="relative max-w-4xl mx-auto">
        <div className="mb-8">
          <div className="flex items-center justify-center relative">
            <button
              type="button"
              onClick={() => navigate('/crm/fulfillment/marketing')}
              className={`absolute left-0 flex items-center justify-center w-10 h-10 rounded-md transition-colors ${darkMode ? 'text-gray-300 hover:bg-gray-700 hover:text-white' : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'}`}
              title="Back to Marketing Fulfillment"
            >
              <FaArrowLeft className="w-5 h-5" />
            </button>
            <div className="text-center">
              <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-gray-900 dark:text-white mb-1">
                Google My Business – {companyName}
              </h1>
              <p className="text-gray-600 dark:text-gray-300 text-sm">
                Access &amp; Logins
              </p>
            </div>
          </div>
        </div>

        <div className={`mb-6 bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm`}>
          <div className="px-6 py-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <span className={`block text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Status</span>
              <p className={`text-xs mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                Track this client&apos;s Google Business Profile fulfillment progress.
              </p>
            </div>
            <div className="flex items-center gap-3">
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                className={`px-3 py-2 rounded-xl text-sm border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white shadow-sm`}
              >
                <option value="Not Started">Not Started</option>
                <option value="Requested">Requested</option>
                <option value="In Progress">In Progress</option>
                <option value="Live">Live</option>
              </select>
              <button
                type="button"
                onClick={handleSaveStatus}
                disabled={savingStatus || !requestId}
                className="px-4 py-2 rounded-md bg-orange-600 text-white text-sm font-medium hover:bg-orange-700 disabled:opacity-50"
              >
                {savingStatus ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </div>

        <section className={`bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm overflow-hidden`}>
          <div className={`px-6 py-4 border-b flex items-center justify-center gap-2 ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
            <FaKey className="w-5 h-5 text-orange-500" />
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Access &amp; Logins</h2>
          </div>
          <div className="p-6 space-y-6">
            <div>
              <h3 className={`text-sm font-semibold uppercase tracking-wide mb-3 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>10. Google Business Profile Access</h3>
              <p className={`text-sm mb-2 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                Admin access must be granted to parker@reignmediagroup.co so we can optimize listings, manage reviews, and post updates.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Item label="Access granted" value={reviews.googleBusinessProfileAccessSent ? 'Yes' : 'No'} darkMode={darkMode} />
                <Item label="Note" value={reviews.googleBusinessProfileAccess} darkMode={darkMode} className="sm:col-span-2" />
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

function Item({ label, value, darkMode, className = '' }) {
  return (
    <div className={className}>
      <span className={`block text-sm font-medium ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>{label}</span>
      <p className={`text-sm mt-0.5 ${darkMode ? 'text-gray-300' : 'text-gray-900'}`}>{value === undefined || value === null || value === '' ? '—' : String(value)}</p>
    </div>
  );
}

export default MarketingFulfillmentReviewsClient;
